import type { MutationFunction, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { BodyType, ErrorType } from '../axios';
import { request } from '../axios';
import type { AddressDto } from '../generated/model';

type RequestOptions = Parameters<typeof request>[1];

const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';
const mutationIdempotencyKeys = new WeakMap<object, string>();

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getMutationIdempotencyKey(variables: object) {
  const existingKey = mutationIdempotencyKeys.get(variables);

  if (existingKey) {
    return existingKey;
  }

  const key = createIdempotencyKey();
  mutationIdempotencyKeys.set(variables, key);

  return key;
}

function createAddressRequestOptions(options: RequestOptions, idempotencyKey: string): RequestOptions {
  return {
    ...options,
    headers: {
      ...(options?.headers as Record<string, string> | undefined),
      'Content-Type': 'application/json',
      [IDEMPOTENCY_KEY_HEADER]: idempotencyKey,
    },
  };
}

export const createAddress = (
  addressDto: BodyType<AddressDto>,
  options?: RequestOptions,
  signal?: AbortSignal,
  idempotencyKey = createIdempotencyKey(),
) => {
  return request<string>(
    {
      url: `/api/addresses`,
      method: 'POST',
      data: addressDto,
      signal,
    },
    createAddressRequestOptions(options, idempotencyKey),
  );
};

export const getCreateAddressMutationOptions = <
  TData = Awaited<ReturnType<typeof createAddress>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { data: BodyType<AddressDto> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['createAddress'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAddress>>, { data: BodyType<AddressDto> }> = (
    props,
  ) => {
    const { data } = props ?? {};

    return createAddress(data, requestOptions, undefined, getMutationIdempotencyKey(props));
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { data: BodyType<AddressDto> },
    TContext
  >;
};

export type CreateAddressMutationResult = NonNullable<Awaited<ReturnType<typeof createAddress>>>;
export type CreateAddressMutationBody = BodyType<AddressDto>;
export type CreateAddressMutationError = ErrorType<unknown>;

export const useCreateAddress = <
  TData = Awaited<ReturnType<typeof createAddress>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { data: BodyType<AddressDto> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { data: BodyType<AddressDto> }, TContext> => {
  const mutationOptions = getCreateAddressMutationOptions(options);

  return useMutation(mutationOptions);
};

export const updateAddress = (addressDto: BodyType<AddressDto>, options?: RequestOptions) => {
  return request<void>(
    {
      url: `/api/addresses`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: addressDto,
    },
    options,
  );
};

export const getUpdateAddressMutationOptions = <
  TData = Awaited<ReturnType<typeof updateAddress>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { data: BodyType<AddressDto> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateAddress'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAddress>>, { data: BodyType<AddressDto> }> = (
    props,
  ) => {
    const { data } = props ?? {};

    return updateAddress(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { data: BodyType<AddressDto> },
    TContext
  >;
};

export type UpdateAddressMutationResult = NonNullable<Awaited<ReturnType<typeof updateAddress>>>;
export type UpdateAddressMutationBody = BodyType<AddressDto>;
export type UpdateAddressMutationError = ErrorType<unknown>;

export const useUpdateAddress = <
  TData = Awaited<ReturnType<typeof updateAddress>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { data: BodyType<AddressDto> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { data: BodyType<AddressDto> }, TContext> => {
  const mutationOptions = getUpdateAddressMutationOptions(options);

  return useMutation(mutationOptions);
};
